import * as React from 'react';
import {
    Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem,
    Stack, TextField, Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { createVirtualMatch, getVirtualProviderLeaguesForSport } from '../../Api';
import { VirtualMatchScheduleResultDto, VirtualProviderLeagueDto, VirtualProviderTeamDto } from '../../models/dtos/VirtualProviderCatalogDto';
import { VIRTUAL_FOOTBALL_ID, VirtualCatalogSportDto } from '../../models/dtos/VirtualSportsCatalogDto';
import VirtualTeamSearchSelect from './VirtualTeamSearchSelect';

function defaultStartDate(): string {
    const date = new Date(Date.now() + 10 * 60 * 1000);
    date.setSeconds(0, 0);
    const pad = (value: number) => String(value).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export default function VirtualMatchCreationDialog(props: {
    open: boolean;
    sports: VirtualCatalogSportDto[];
    onClose: () => void;
    onCreated: (result: VirtualMatchScheduleResultDto) => void;
}) {
    const [sportId, setSportId] = React.useState<number | ''>('');
    const [leagues, setLeagues] = React.useState<VirtualProviderLeagueDto[]>([]);
    const [loadingLeagues, setLoadingLeagues] = React.useState(false);
    const [leagueId, setLeagueId] = React.useState('');
    const [homeTeam, setHomeTeam] = React.useState<VirtualProviderTeamDto | null>(null);
    const [awayTeam, setAwayTeam] = React.useState<VirtualProviderTeamDto | null>(null);
    const [startDate, setStartDate] = React.useState(defaultStartDate());
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState('');
    const [created, setCreated] = React.useState<VirtualMatchScheduleResultDto | null>(null);

    React.useEffect(() => {
        if (!props.open) return;
        setSportId(props.sports.some(sport => sport.id === VIRTUAL_FOOTBALL_ID) ? VIRTUAL_FOOTBALL_ID : '');
        setLeagues([]);
        setLeagueId('');
        setHomeTeam(null);
        setAwayTeam(null);
        setStartDate(defaultStartDate());
        setBusy(false);
        setError('');
        setCreated(null);
    }, [props.open, props.sports]);

    React.useEffect(() => {
        if (!props.open || sportId === '') {
            setLeagues([]);
            return;
        }
        let cancelled = false;
        setLoadingLeagues(true);
        setLeagueId('');
        setHomeTeam(null);
        setAwayTeam(null);
        getVirtualProviderLeaguesForSport(sportId)
            .then(result => {
                if (!cancelled) setLeagues(result.filter(league => league.leagueType === 'FRIENDLY' && league.active));
            })
            .catch(reason => { if (!cancelled) setError(reason instanceof Error ? reason.message : 'Could not load leagues'); })
            .finally(() => { if (!cancelled) setLoadingLeagues(false); });
        return () => { cancelled = true; };
    }, [props.open, sportId]);

    const startDateValid = startDate.trim().length > 0 && !Number.isNaN(new Date(startDate).getTime());
    const teamsValid = homeTeam != null && awayTeam != null && homeTeam.id !== awayTeam.id;
    const valid = sportId !== '' && leagueId !== '' && teamsValid && startDateValid;

    const schedule = async () => {
        if (sportId === '' || !valid) return;
        setBusy(true);
        setError('');
        try {
            const result = await createVirtualMatch(sportId, {
                leagueId,
                homeTeamId: homeTeam!.id,
                awayTeamId: awayTeam!.id,
                startDate: new Date(startDate).toISOString(),
            });
            setCreated(result);
            props.onCreated(result);
        } catch (reason) {
            setError(reason instanceof Error ? reason.message : 'Could not schedule the match');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={props.open} onClose={busy ? undefined : props.onClose} fullWidth maxWidth="sm">
            <DialogTitle>Create virtual match</DialogTitle>
            <DialogContent>
                {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
                {created ? (
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Alert severity="success">Match scheduled.</Alert>
                        <Typography variant="h6">{created.match.homeTeam.name} vs {created.match.awayTeam.name}</Typography>
                        <Typography color="text.secondary">
                            Kicks off {new Date(created.match.startDate).toLocaleString()}
                        </Typography>
                    </Stack>
                ) : (
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            select
                            required
                            label="Virtual sport"
                            value={sportId}
                            disabled={busy}
                            onChange={event => setSportId(Number(event.target.value))}
                        >
                            {props.sports.map(sport => <MenuItem key={sport.id} value={sport.id}>{sport.name}</MenuItem>)}
                        </TextField>
                        <TextField
                            select
                            required
                            label="League"
                            value={leagueId}
                            disabled={busy || sportId === '' || loadingLeagues}
                            onChange={event => setLeagueId(event.target.value)}
                            helperText={sportId !== '' && !loadingLeagues && leagues.length === 0
                                ? 'No active friendly leagues exist yet for this sport.'
                                : 'Manual matches can only be scheduled in friendly leagues.'}
                        >
                            {leagues.map(league => <MenuItem key={league.id} value={league.id}>{league.name}</MenuItem>)}
                        </TextField>
                        <VirtualTeamSearchSelect
                            label="Home team"
                            value={homeTeam}
                            onChange={team => {
                                setHomeTeam(team);
                                if (team != null && awayTeam?.id === team.id) setAwayTeam(null);
                            }}
                            disabled={busy || sportId === ''}
                            sportId={sportId === '' ? undefined : sportId}
                        />
                        <VirtualTeamSearchSelect
                            label="Away team"
                            value={awayTeam}
                            onChange={setAwayTeam}
                            disabled={busy || sportId === ''}
                            sportId={sportId === '' ? undefined : sportId}
                            excludedTeamIds={homeTeam == null ? [] : [homeTeam.id]}
                        />
                        <TextField
                            required
                            type="datetime-local"
                            label="Start date"
                            value={startDate}
                            disabled={busy}
                            onChange={event => setStartDate(event.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                {created ? (
                    <>
                        <Button onClick={props.onClose}>Done</Button>
                        <Button
                            variant="contained"
                            startIcon={<OpenInNewIcon />}
                            onClick={() => window.open(created.liveUrl, '_blank', 'noopener,noreferrer')}
                        >
                            Live match
                        </Button>
                    </>
                ) : (
                    <>
                        <Button onClick={props.onClose} disabled={busy}>Cancel</Button>
                        <Button variant="contained" disabled={!valid || busy} onClick={schedule}>
                            {busy ? <CircularProgress size={20} /> : 'Schedule match'}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
}
